import { useParams, useLocation } from "react-router-dom";
import { Checkbox } from "antd";
import { useState, useEffect } from "react";
import { db } from "../data/base";
import { doc, getDoc } from "firebase/firestore";

import { primaryShades, secondaryShades } from "../theme/theme";
import arShadesLogo from "../assets/images/logo/logo_arshades.svg";
import styles from "../styles/ARShadesDemo/ARShadesDemo.module.css";

const VIEWER_ID = 'Eo2MPry6FRmBzeiOtbv5';

const ARShadesDemo = () => {
    const params = useParams();
    const tokenProductId = params.TokenProductId;

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const fovParam = queryParams.get("fov");
    const fovMobileParam = queryParams.get("fovMobile");

    const multipleViewerUrl = `${process.env.REACT_APP_ACTIVE_BASE_URL}/Arshades3ds/${VIEWER_ID}/glasses/${tokenProductId}`;
    const singleViewerUrl = `${process.env.REACT_APP_ACTIVE_BASE_URL}/Arshades3d/${VIEWER_ID}/glasses/${tokenProductId}/variant/`;

    const { primary300 } = primaryShades;
    const { secondary200 } = secondaryShades;

    const [iframeUrl, setIframeUrl] = useState('');
    const [listVariants, setListVariants] = useState([]);
    const [useReflect, setUseReflect] = useState(false);
    const [currentSin, setCurrentSin] = useState("1"); // "1" = single, "0" = multiple

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);

    useEffect(() => {
        if (!tokenProductId) return;

        async function fetchVariantsData() {
            try {
                const productRef = doc(db, "3DViewers", VIEWER_ID, "Products", tokenProductId);
                let productSnap = await getDoc(productRef);
                // console.log(productSnap.data());

                if (!productSnap.exists() || !productSnap.data()) {
                    console.error("Product not found!");
                    return;
                }

                const data = productSnap.data();

                if (data.list_variants) {
                    setListVariants(data.list_variants);
                }

            } catch (error) {
                console.error("Errore durante l'inizializzazione del visualizzatore:", error);
            }
        }

        fetchVariantsData();
    }, [tokenProductId]);

    useEffect(() => {
        if (listVariants.length > 0) {
            setIframeUrl(buildViewerUrl(singleViewerUrl + listVariants[0], { useReflect, fov: fovParam, fovMobileParam: fovMobileParam }));
        }
    }, [listVariants]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    function buildViewerUrl(baseUrl, { useReflect, fov, fovMobileParam }) {
        const params = new URLSearchParams();
        if (useReflect) params.set('reflect', 'true');
        if (fov !== undefined && fov !== null && fov !== '') params.set('fov', fov);
        if (fovMobileParam !== undefined && fovMobileParam !== null && fovMobileParam !== '') params.set('fovMobile', fovMobileParam);
        // Se ci sono parametri, aggiungili alla fine dell'URL
        return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
    }

    const handleViewModeChange = (mode) => {
        let url = '';
        if (mode === 'single') {
            setCurrentSin("1");
            url = buildViewerUrl(singleViewerUrl + listVariants[0], { useReflect, fov: fovParam, fovMobileParam: fovMobileParam });
        } else {
            setCurrentSin("0");
            url = buildViewerUrl(multipleViewerUrl, { useReflect, fov: fovParam, fovMobileParam: fovMobileParam });
        }
        setIframeUrl(url);
    };

    const handleReflectingFloorChange = (e) => {
        const newUseReflect = e.target.checked;
        setUseReflect(newUseReflect);
        // Ricostruisci l'url in base alla modalità attuale
        let url = '';
        if (currentSin === "1") {
            url = buildViewerUrl(singleViewerUrl + listVariants[0], { useReflect: newUseReflect, fov: fovParam, fovMobileParam: fovMobileParam });
        } else {
            url = buildViewerUrl(multipleViewerUrl, { useReflect: newUseReflect, fov: fovParam, fovMobileParam: fovMobileParam });
        }
        setIframeUrl(url);
    }

    const toggleMobileModal = () => setIsMobileModalOpen(!isMobileModalOpen);
    const handleMobileModalClose = (e) => {
        if (e.target === e.currentTarget) setIsMobileModalOpen(false);
    };

    function getIframeHeight(url) {
        if (window.innerWidth <= 700) {
            return url.includes('/variant/') ? "91.5vh" : "97vh";
        }
        return "100vh";
    }

    const DesktopMenu = () => (
        <div
            className={styles.demoMenu}
            style={{
                '--primary-color': primary300,
                '--secondary-color': secondary200,
            }}
        >
            <div className={styles.demoMenuContent}>
                <div className={styles.demoMenuBrand}>
                    <img src={arShadesLogo} alt="ARS Logo" className={styles.demoLogo} />
                    <span className={styles.demoLabel}>Demo</span>
                </div>
                <div className={styles.demoMenuDivider}></div>
                <div className={styles.demoMenuSection}>
                    <div className={styles.toggleSwitch}>
                        <span className={`${styles.toggleLabel} ${currentSin === '1' ? styles.active : ''}`}>
                            Single Variant
                        </span>
                        <div
                            className={`${styles.toggleContainer} ${currentSin !== '1' ? styles.allVariantsActive : ''}`}
                            onClick={() => handleViewModeChange(currentSin === '1' ? 'multiple' : 'single')}
                        >
                            <div className={`${styles.toggleSlider} ${currentSin === '1' ? styles.left : styles.rightAllVariants}`}></div>
                        </div>
                        <span className={`${styles.toggleLabel} ${currentSin !== '1' ? styles.active : ''}`}>
                            All Variants
                        </span>
                    </div>
                </div>
                <div className={styles.demoMenuDivider}></div>
                <div className={styles.demoMenuSection}>
                    <span className={styles.demoMenuLabel}>Reflecting floor</span>
                    <Checkbox
                        onChange={handleReflectingFloorChange}
                        checked={useReflect}
                    />
                </div>
            </div>
        </div>
    );

    const MobileMenu = () => (
        <>
            <div className={styles.demoMenuMobile}>
                <div className={styles.mobileToggleButton} onClick={toggleMobileModal}>
                    <svg className={styles.gearIcon} viewBox="0 0 24 24">
                        <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.98C19.47,12.66 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11.02L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11.02C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.66 4.57,12.98L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.04 4.95,18.95L7.44,17.95C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.95L19.05,18.95C19.27,19.04 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.98Z" />
                    </svg>
                    <span className={styles.mobileButtonText}>Demo</span>
                </div>
            </div>
            {isMobileModalOpen && (
                <div className={styles.mobileModal} onClick={handleMobileModalClose}>
                    <div className={styles.mobileModalContent}>
                        <button
                            className={styles.mobileModalClose}
                            onClick={() => setIsMobileModalOpen(false)}
                            aria-label="Chiudi"
                        >
                            <svg
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className={styles.mobileModalCloseIcon}
                                style={{ cursor: "pointer", transition: "opacity 0.2s", opacity: 1 }}
                            >
                                <circle
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="#46518a"
                                    strokeWidth="2"
                                    fill="rgba(255, 255, 255, 0.9)"
                                />
                                <path
                                    d="M15 9l-6 6M9 9l6 6"
                                    stroke="#46518a"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        <div className={styles.mobileModalHeader}>
                            <img src={arShadesLogo} alt="ARS Logo" className={styles.mobileModalLogo} />
                            <span className={styles.mobileModalTitle}>Demo</span>
                        </div>
                        <div className={styles.mobileModalRow}>
                            <div className={styles.toggleSwitch}>
                                <span className={`${styles.toggleLabel} ${currentSin === '1' ? styles.active : ''}`}>
                                    Single Variant
                                </span>
                                <div
                                    className={`${styles.toggleContainer} ${currentSin !== '1' ? styles.allVariantsActive : ''}`}
                                    onClick={() => handleViewModeChange(currentSin === '1' ? 'multiple' : 'single')}
                                >
                                    <div className={`${styles.toggleSlider} ${currentSin === '1' ? styles.left : styles.rightAllVariants}`}></div>
                                </div>
                                <span className={`${styles.toggleLabel} ${currentSin !== '1' ? styles.active : ''}`}>
                                    All Variants
                                </span>
                            </div>
                        </div>
                        <div className={styles.mobileModalRow}>
                            <span className={styles.mobileRowLabel}>Reflecting floor</span>
                            <Checkbox
                                onChange={handleReflectingFloorChange}
                                checked={useReflect}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    return (
        <>
            {isMobile ? <MobileMenu /> : <DesktopMenu />}
            {/* Iframe a schermo intero */}
            {iframeUrl && (
                <div
                    style={{
                        width: "100vw",
                        height: "100vh",
                        margin: 0,
                        padding: 0,
                        overflow: "hidden",
                    }}
                >
                    <iframe
                        src={iframeUrl}
                        title="ARShades 3D Viewer"
                        width="100%"
                        height="100%"
                        style={{
                            border: "none",
                            width: "100vw",
                            height: getIframeHeight(iframeUrl),
                            display: "block",
                            overflow: "hidden",
                        }}
                        allowFullScreen
                    ></iframe>
                </div>
            )}
        </>
    );
};

export default ARShadesDemo;